import { Box } from 'src/design/atoms/Box';
import { FrozenPane } from 'src/design/atoms/FrozenPane';
import { Grid } from 'src/design/atoms/Grid';
import { Loader } from 'src/design/atoms/Loader';
import { Progress } from 'src/design/atoms/Progress';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { useFrozenPane } from 'src/design/molecules/useFrozenPane';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PostDto, PostFilterDto } from 'src/api/FiretrolApiClient';
import { usePostSearch } from 'src/api/queries/posts/usePostSearch';
import { EditPost } from 'src/routes/Jobs/ActivityFeed/components/EditPost';
import { Filters } from 'src/routes/Jobs/ActivityFeed/components/Filters';
import { PostCard } from 'src/routes/Jobs/ActivityFeed/components/PostCard';
import { PostItem } from 'src/routes/Jobs/ActivityFeed/components/PostItem';
import { PostViewDialog } from 'src/routes/Jobs/ActivityFeed/components/PostViewDialog';

const FrozenFeedHeader: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const pane = useFrozenPane();
  return (
    <FrozenPane
      ref={pane.ref}
      frozen={pane.frozen}
      data-testid="activity-feed-frozen-header"
    >
      {children}
    </FrozenPane>
  );
};

export const ActivityFeed: React.FC<{
  jobId: number;
  jobNumber?: string;
  disablePadding?: boolean;
  defaultAll?: boolean;
  defaultCTC?: boolean;
  /** When set, this header and the post composer stay pinned while the feed scrolls. */
  stickyHeader?: React.ReactNode;
}> = ({
  jobId,
  jobNumber,
  disablePadding = false,
  defaultAll = false,
  defaultCTC = false,
  stickyHeader,
}) => {
  const { id: idParam }: any = useParams();
  const id = idParam || jobNumber || undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [postViewOpen, setPostViewOpen] = React.useState(false);
  const [sort, setSort] = React.useState('updated_at');
  const [filter, setFilter] = React.useState('all');
  const [postType, setPostType] = React.useState('all');
  const [query, setQuery] = React.useState('');
  const [openPost, setOpenPost] = React.useState<PostDto | null>(null);
  const [filters, setFilters] = React.useState<PostFilterDto>({
    visibility: 'all',
    resolved: defaultAll ? 'all' : 'unresolved',
    ctc: defaultCTC,
    assignedToMe: false,
    createdByMe: false,
    constraint: false,
    critical: false,
    cost: false,
    assigned: false,
  });

  const { data, isLoading, hasNextPage, fetchNextPage, refetch } =
    usePostSearch(id ? jobId : null, { filters, sort, postType, query });

  const posts = React.useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const onFilterChange = (key: string, value: string | boolean) => {
    setFilters((prevState) => ({ ...prevState, [key]: value }));
  };

  React.useEffect(() => {
    if (params.get('postId')) {
      params.delete('postId');
      navigate(location.pathname);
      setPostViewOpen(true);
    }
  }, [params]);

  const renderPosts = () => {
    if (posts.length === 0) {
      return <Text ta="center">No posts matching your filters</Text>;
    }
    return posts.map((post) => (
      <Box key={post.id} mb="md">
        <PostItem item={post} openEditDialog={setOpenPost} jobId={jobId} />
      </Box>
    ));
  };

  const openEditFromView = (item: PostDto) => {
    params.delete('postId');
    setPostViewOpen(false);
    setOpenPost(item);
  };

  const postCard = (
    <PostCard onPostCreated={() => setOpenPost(null)} jobId={jobId} />
  );

  return (
    <Stack gap="md">
      <PostViewDialog
        onClose={() => setPostViewOpen(false)}
        jobId={jobId}
        postId={params.get('postId')}
        open={postViewOpen}
        openEditDialog={openEditFromView}
      />
      {stickyHeader === undefined ? (
        postCard
      ) : (
        <FrozenFeedHeader>
          {stickyHeader}
          {postCard}
        </FrozenFeedHeader>
      )}
      <Filters
        filters={filters}
        sort={sort}
        onFiltersChange={onFilterChange}
        onSortChange={(value) => setSort(value)}
        postType={postType}
        onPostTypeChange={(value) => setPostType(value)}
        filter={filter}
        query={query}
        onFilterChange={(value) => setFilter(value)}
        onQueryChange={(value) => setQuery(value)}
      />
      <EditPost
        post={openPost}
        handleClose={() => setOpenPost(null)}
        onPostSaved={() => setOpenPost(null)}
      />
      <Grid>
        <Grid.Col span={{ base: 12, lg: disablePadding ? 12 : 6 }}>
          {isLoading ? (
            <Loader />
          ) : (
            <InfiniteScroll
              dataLength={posts.length}
              next={() => fetchNextPage()}
              hasMore={hasNextPage}
              loader={<Progress value={100} animated />}
              refreshFunction={() => refetch()}
              pullDownToRefresh={false}
              pullDownToRefreshThreshold={50}
              pullDownToRefreshContent={
                <Text ta="center">↓ Pull down to refresh</Text>
              }
              releaseToRefreshContent={
                <Text ta="center">↑ Release to refresh</Text>
              }
            >
              {renderPosts()}
            </InfiniteScroll>
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
